/**
 * US States and territories constant for use in dropdowns and forms
 */
export const US_STATES = [
	{ value: 'AL', label: 'Alabama' },
	{ value: 'AK', label: 'Alaska' },
	{ value: 'AZ', label: 'Arizona' },
	{ value: 'AR', label: 'Arkansas' },
	{ value: 'CA', label: 'California' },
	{ value: 'CO', label: 'Colorado' },
	{ value: 'CT', label: 'Connecticut' },
	{ value: 'DE', label: 'Delaware' },
	{ value: 'DC', label: 'District of Columbia' },
	{ value: 'FL', label: 'Florida' },
	{ value: 'GA', label: 'Georgia' },
	{ value: 'HI', label: 'Hawaii' },
	{ value: 'ID', label: 'Idaho' },
	{ value: 'IL', label: 'Illinois' },
	{ value: 'IN', label: 'Indiana' },
	{ value: 'IA', label: 'Iowa' },
	{ value: 'KS', label: 'Kansas' },
	{ value: 'KY', label: 'Kentucky' },
	{ value: 'LA', label: 'Louisiana' },
	{ value: 'ME', label: 'Maine' },
	{ value: 'MD', label: 'Maryland' },
	{ value: 'MA', label: 'Massachusetts' },
	{ value: 'MI', label: 'Michigan' },
	{ value: 'MN', label: 'Minnesota' },
	{ value: 'MS', label: 'Mississippi' },
	{ value: 'MO', label: 'Missouri' },
	{ value: 'MT', label: 'Montana' },
	{ value: 'NE', label: 'Nebraska' },
	{ value: 'NV', label: 'Nevada' },
	{ value: 'NH', label: 'New Hampshire' },
	{ value: 'NJ', label: 'New Jersey' },
	{ value: 'NM', label: 'New Mexico' },
	{ value: 'NY', label: 'New York' },
	{ value: 'NC', label: 'North Carolina' },
	{ value: 'ND', label: 'North Dakota' },
	{ value: 'OH', label: 'Ohio' },
	{ value: 'OK', label: 'Oklahoma' },
	{ value: 'OR', label: 'Oregon' },
	{ value: 'PA', label: 'Pennsylvania' },
	{ value: 'PR', label: 'Puerto Rico' },
	{ value: 'RI', label: 'Rhode Island' },
	{ value: 'SC', label: 'South Carolina' },
	{ value: 'SD', label: 'South Dakota' },
	{ value: 'TN', label: 'Tennessee' },
	{ value: 'TX', label: 'Texas' },
	{ value: 'UT', label: 'Utah' },
	{ value: 'VT', label: 'Vermont' },
	{ value: 'VI', label: 'Virgin Islands' },
	{ value: 'VA', label: 'Virginia' },
	{ value: 'WA', label: 'Washington' },
	{ value: 'WV', label: 'West Virginia' },
	{ value: 'WI', label: 'Wisconsin' },
	{ value: 'WY', label: 'Wyoming' },
] as const;

export type USStateCode = (typeof US_STATES)[number]['value'];
export type USStateName = (typeof US_STATES)[number]['label'];

/**
 * Canonical whitelist of valid USPS jurisdiction codes we accept in license
 * data: the 50 states + DC (and PR/VI, already in US_STATES), the remaining
 * inhabited US territories, and the three military "state" codes.
 *
 * Used as the single source of truth to (1) constrain the registry State
 * filter dropdown and (2) drop out-of-range codes at ingestion (PD-14489),
 * so non-US values a source feed emits — e.g. NPPES 'ZZ' (foreign),
 * Canadian provinces 'ON'/'QC', or truncated foreign codes — never enter
 * the registry or surface in the UI.
 */
export const US_JURISDICTION_CODES: ReadonlyArray<string> = [
	...US_STATES.map((s) => s.value),
	'GU', // Guam
	'AS', // American Samoa
	'MP', // Northern Mariana Islands
	'AA', // Armed Forces Americas
	'AE', // Armed Forces Europe
	'AP', // Armed Forces Pacific
];

const US_JURISDICTION_SET = new Set<string>(US_JURISDICTION_CODES);

/**
 * True when `code` is a valid US jurisdiction (case/space-insensitive).
 * Non-US / corrupted codes return false so callers can drop or hide them.
 */
export function isUsJurisdictionCode(code: string | null | undefined): boolean {
	if (code == null) return false;
	return US_JURISDICTION_SET.has(code.trim().toUpperCase());
}

/**
 * Get state name by code
 */
export function getStateName(code: string): string | undefined {
	return US_STATES.find((state) => state.value === code)?.label;
}

/**
 * Get state code by name
 */
export function getStateCode(name: string): string | undefined {
	return US_STATES.find((state) => state.label === name)?.value;
}
